import React, { useCallback, useState } from 'react';
import { Col, Container, Row } from 'reactstrap';
import {
  PeriodMetrics,
  ProperAssignedVisit,
  ProperPreSaleMetrics,
  ProperPreSaleVisit,
  ProperV2Metrics,
} from '../models/dashboardModels';
import useAdminEntity from '../../../hooks/useAdminEntity';
import {
  getDashboardMetricsPreSaleEndpoint,
  getDashboardMetricsV2Endpoint,
  getDashboardPreSaleVisitsEndpoint,
  getDashboardVisitsEndpoint,
} from '../services/dashboardService';
import useAuth from '../../../hooks/useAuth';
import useAdminPagination from '../../../hooks/useAdminPagination';
import AdminPaginator from '../../../components/admin/AdminPaginator';
import DashboardVisitListTable from './DashboardVisitsListTable';
import DashboardPreSaleVisitListTable from './DashboardPreSaleVisitsListTable';
import {
  toStatsMetrics,
  VISIT_STATUS_MAP,
  PRE_SALE_STATUS_MAP,
} from '../adapters/dashboardAdapters';
import MetricsDashboard from './MetricsDashboard';
import NavigationTabDashboard from './NavigationTabDashboard';
import { activeTab } from '../types/dashboard.types';
import { IconMessage } from '@tabler/icons-react';
import usePendingFeedbackVisits from '../hooks/usePendingFeedbackVisits';
import { VISIT_STATUS, VisitPreSaleState } from '../../../constants/visitsConstants';
import DashboardFilterBar, { DashboardViewMode } from './DashboardFilterBar';
import DashboardVisitsCalendar from './DashboardVisitsCalendar';
import {
  preSaleVisitsToCalendarEvents,
  rentVisitsToCalendarEvents,
} from '../adapters/calendarEventsAdapter';
import { useNavigate } from 'react-router-dom';

// Cantidad de visitas que se piden de una vez; el filtro por estado y la
// paginación se resuelven en el cliente.
const PAGE_FETCH_SIZE = 100;
const PAGE_SIZE = 10;
// Valor especial del filtro: finalizadas sin feedback del Proper.
const MISSING_FEEDBACK = 'missing_feedback';

const ProperDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<DashboardViewMode>('list');
  const [page, setPage] = useState(1);
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [activeTab, setActiveTabState] = useState<activeTab>('rent');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('');

  const handleDateRangeChange = (newStart: string, newEnd: string) => {
    setPage(1);
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const handleStatusChange = (value: string) => {
    setPage(1);
    setStatus(value);
  };

  const handleClearFilters = () => {
    setPage(1);
    setStartDate('');
    setEndDate('');
    setStatus('');
  };

  const setActiveTab = (tab: activeTab) => {
    setActiveTabState(tab);
    handleClearFilters();
  };

  const defaultPeriod: PeriodMetrics = {
    end: '',
    start: '',
  };

  const getDashboardMetrics = useCallback(
    () =>
      getDashboardMetricsV2Endpoint(token, 'proper', undefined, {
        startDate: activeTab === 'rent' ? startDate || undefined : undefined,
        endDate: activeTab === 'rent' ? endDate || undefined : undefined,
      }),
    [token, activeTab, startDate, endDate],
  );
  const [dashboardMetrics] = useAdminEntity<ProperV2Metrics>(
    getDashboardMetrics,
    console.error,
  );

  const getSecondaryMetrics = useCallback(
    () =>
      getDashboardMetricsV2Endpoint(token, 'proper', 'on_sale', {
        startDate:
          activeTab === 'secondary' ? startDate || undefined : undefined,
        endDate: activeTab === 'secondary' ? endDate || undefined : undefined,
      }),
    [token, activeTab, startDate, endDate],
  );
  const [secondaryMetrics] = useAdminEntity<ProperV2Metrics>(
    getSecondaryMetrics,
    console.error,
  );

  const getDashboardPreSaleMetrics = useCallback(
    () =>
      getDashboardMetricsPreSaleEndpoint(token, 'proper', {
        startDate:
          activeTab === 'pre-sale' ? startDate || undefined : undefined,
        endDate: activeTab === 'pre-sale' ? endDate || undefined : undefined,
      }),
    [token, activeTab, startDate, endDate],
  );

  const [dashboardPreSaleMetrics] = useAdminEntity<ProperPreSaleMetrics>(
    getDashboardPreSaleMetrics,
    console.error,
  );

  const [dashboardVisits, setDashboardVisits] = useState<ProperAssignedVisit[]>(
    [],
  );
  const getDashboardVisits = useCallback(
    (queryParams: string, token: string) => {
      const extraParams: string[] = [];
      const isRentOrSecondary = activeTab === 'rent' || activeTab === 'secondary';
      if (isRentOrSecondary && startDate)
        extraParams.push(`start_date=${startDate}`);
      if (isRentOrSecondary && endDate)
        extraParams.push(`end_date=${endDate}`);
      extraParams.push(
        `marketplace_type=${activeTab === 'secondary' ? 'on_sale' : 'on_rent'}`,
      );
      // El endpoint del Proper rechaza `status` (400): se filtra en el cliente.
      extraParams.push(`take=${PAGE_FETCH_SIZE}`);
      const separator = extraParams.length
        ? `${queryParams.includes('?') ? '&' : '?'}${extraParams.join('&')}`
        : '';
      return getDashboardVisitsEndpoint(
        `${queryParams}${separator}`,
        token,
      );
    },
    [activeTab, startDate, endDate],
  );
  useAdminPagination(
    getDashboardVisits,
    setDashboardVisits,
    console.error,
  );

  const [dashboardPreSaleVisits, setDashboardPreSaleVisits] = useState<
    ProperPreSaleVisit[]
  >([]);
  const getDashboardPreSaleVisits = useCallback(
    (queryParams: string, token: string) => {
      const extraParams: string[] = [];
      if (activeTab === 'pre-sale' && startDate)
        extraParams.push(`start_date=${startDate}`);
      if (activeTab === 'pre-sale' && endDate)
        extraParams.push(`end_date=${endDate}`);
      extraParams.push(`take=${PAGE_FETCH_SIZE}`);
      const separator = extraParams.length
        ? `${queryParams.includes('?') ? '&' : '?'}${extraParams.join('&')}`
        : '';
      return getDashboardPreSaleVisitsEndpoint(
        `${queryParams}${separator}`,
        token,
      );
    },
    [activeTab, startDate, endDate],
  );
  useAdminPagination(
      getDashboardPreSaleVisits,
      setDashboardPreSaleVisits,
      console.error,
    );

  const { pendingIds } = usePendingFeedbackVisits(
    dashboardPreSaleVisits
      .filter((visit) => visit.visitInfo.status === VisitPreSaleState.DONE)
      .map((visit) => visit.id),
    activeTab === 'pre-sale',
  );

  if (!dashboardMetrics || !secondaryMetrics || !dashboardPreSaleMetrics) {
    return <>Cargando</>;
  }

  const rentalInfo = toStatsMetrics(dashboardMetrics.visits, VISIT_STATUS_MAP);
  const secondaryInfo = toStatsMetrics(
    secondaryMetrics.visits,
    VISIT_STATUS_MAP,
  );
  const preSaleInfo = toStatsMetrics(dashboardPreSaleMetrics.visits, PRE_SALE_STATUS_MAP);

  const footerFor = (
    page: number,
    totalPages: number,
    onPageChange: (page: number) => void,
  ) => (
    <AdminPaginator
      page={page}
      totalPages={totalPages}
      onPageChange={onPageChange}
    />
  );

  // `dashboardVisits` ya viene filtrado por `marketplace_type` según la pestaña.
  const filterByStatus = (visits: ProperAssignedVisit[]) =>
    !status
      ? visits
      : status === MISSING_FEEDBACK
        ? visits.filter(
            (visit) =>
              visit.visitInfo.status === VISIT_STATUS.DONE &&
              visit.visitInfo.has_feedback === false,
          )
        : visits.filter((visit) => visit.visitInfo.status === status);
  // Si la visita no trae `marketplace_type`, se completa con el de la pestaña
  // (el detalle elige el formulario de feedback según ese valor).
  const tabMarketplaceType = activeTab === 'secondary' ? 'on_sale' : 'on_rent';
  const tabVisits = dashboardVisits.map((visit) =>
    visit.visitInfo.marketplace_type
      ? visit
      : {
          ...visit,
          visitInfo: { ...visit.visitInfo, marketplace_type: tabMarketplaceType },
        },
  );
  const filteredRentVisits =
    activeTab === 'rent' ? filterByStatus(tabVisits) : [];
  const filteredSecondaryVisits =
    activeTab === 'secondary' ? filterByStatus(tabVisits) : [];
  const filteredPreSaleVisits = !status
    ? dashboardPreSaleVisits
    : status === MISSING_FEEDBACK
      ? dashboardPreSaleVisits.filter((visit) => pendingIds.has(visit.id))
      : dashboardPreSaleVisits.filter(
          (visit) => visit.visitInfo.status === status,
        );
  const activeCount =
    activeTab === 'rent'
      ? filteredRentVisits.length
      : activeTab === 'secondary'
        ? filteredSecondaryVisits.length
        : filteredPreSaleVisits.length;
  const totalPages = Math.max(1, Math.ceil(activeCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageSlice = <T,>(items: T[]) =>
    items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const localFooter = footerFor(currentPage, totalPages, setPage);

  const activePeriod =
    activeTab === 'rent'
      ? (dashboardMetrics?.period ?? defaultPeriod)
      : activeTab === 'secondary'
        ? (secondaryMetrics?.period ?? defaultPeriod)
        : (dashboardPreSaleMetrics?.period ?? defaultPeriod);

  const openRentDetail = (id: string) => {
    const visit = [...filteredRentVisits, ...filteredSecondaryVisits].find(
      (item) => item.id === id,
    );
    if (visit)
      navigate(`/dashboard/rent-visits/${visit.id}`, { state: { visit } });
  };
  const openPreSaleDetail = (id: string) => {
    const visit = filteredPreSaleVisits.find((item) => item.id === id);
    if (visit)
      navigate(`/dashboard/presale-visits/${visit.id}`, { state: { visit } });
  };

  return (
    <Container className="mt-6 dashboard-v2" fluid>
      <NavigationTabDashboard
        activeTab={activeTab}
        onTabChange={setActiveTab}
        preSaleCount={preSaleInfo.total}
        rentCount={rentalInfo.total}
        secondaryCount={secondaryInfo.total}
        advisoryCount={0}
        // El dashboard del Proper no implementa el tab de Advisory (ese es
        // exclusivo de InternalUserDashboard, el dashboard de SDR/admin).
        visibleTabs={['rent', 'secondary', 'pre-sale']}
      />

      <MetricsDashboard
        activeTab={activeTab}
        stats={
          activeTab === 'rent'
            ? rentalInfo
            : activeTab === 'secondary'
              ? secondaryInfo
              : preSaleInfo
        }
        selectedStatus={status}
        onSelectStatus={handleStatusChange}
        extraTiles={[
          {
            key: 'missing_feedback',
            label: 'Falta feedback',
            value:
              activeTab === 'rent' || activeTab === 'secondary'
                ? ((activeTab === 'rent'
                    ? dashboardMetrics.missing_feedback
                    : secondaryMetrics.missing_feedback) ??
                  dashboardVisits.filter(
                    (visit) =>
                      visit.visitInfo.status === VISIT_STATUS.DONE &&
                      visit.visitInfo.has_feedback === false,
                  ).length)
                : pendingIds.size,
            status: MISSING_FEEDBACK,
            tone: 'warning',
            icon: <IconMessage size={16} />,
          },
        ]}
      />

      <DashboardFilterBar
        activeTab={activeTab}
        isRestricted={false}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
        status={status}
        onStatusChange={handleStatusChange}
        onClearFilters={handleClearFilters}
        hideAssignmentControl
        defaultPeriod={activePeriod.start ? activePeriod : undefined}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {activeTab === 'rent' && (
        <Row>
          <Col sm={12} className="mb-4">
            {viewMode === 'calendar' ? (
              <DashboardVisitsCalendar
                events={rentVisitsToCalendarEvents(filteredRentVisits)}
                onOpenDetail={openRentDetail}
                footer={localFooter}
              />
            ) : (
              <DashboardVisitListTable
                visits={pageSlice(filteredRentVisits)}
                footer={localFooter}
              />
            )}
          </Col>
        </Row>
      )}

      {activeTab === 'secondary' && (
        <Row>
          <Col sm={12} className="mb-4">
            {viewMode === 'calendar' ? (
              <DashboardVisitsCalendar
                events={rentVisitsToCalendarEvents(filteredSecondaryVisits)}
                onOpenDetail={openRentDetail}
                footer={localFooter}
              />
            ) : (
              <DashboardVisitListTable
                visits={pageSlice(filteredSecondaryVisits)}
                footer={localFooter}
              />
            )}
          </Col>
        </Row>
      )}

      {activeTab === 'pre-sale' && (
        <Row>
          <Col sm={12} className="mb-4">
            {viewMode === 'calendar' ? (
              <DashboardVisitsCalendar
                events={preSaleVisitsToCalendarEvents(filteredPreSaleVisits)}
                onOpenDetail={openPreSaleDetail}
                footer={localFooter}
              />
            ) : (
              <DashboardPreSaleVisitListTable
                visits={pageSlice(filteredPreSaleVisits)}
                pendingFeedbackIds={pendingIds}
                footer={localFooter}
              />
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ProperDashboard;
